import { useLocalization } from 'src/utils/useLocalization';
import React, { useRef, useState } from 'react';

import { FinancingService } from 'src/services/financing.service';
import { isSiteCanadian } from 'src/utils/helpers';
import { useTranslation } from 'react-i18next';
import {
    CANADA,
    ERR_PDF_INNERHTML,
    FINANCING_HANDBOOK,
    HANDBOOK_BRANDS,
    USA,
} from 'src/utils/constants';
import './handbook.scss';

const PDF_MIME_TYPE = 'application/pdf';

/** The US API may answer with a JSON body holding a short-lived S3 link instead of the PDF. */
const getDownloadUrl = async (data: Blob): Promise<string | undefined> => {
    if (data.type === PDF_MIME_TYPE) {
        return undefined;
    }
    try {
        return JSON.parse(await data.text()).downloadUrl;
    } catch {
        return undefined;
    }
};

const Handbook: React.FC<{
    brand: string;
}> = ({ brand }) => {
    const { handbook } = useLocalization();
    const { i18n } = useTranslation();
    const financialService = new FinancingService();
    const language = i18n?.language?.includes('fr') ? 'fr' : 'en';
    const containerClass = `handbook-container ${brand}`;
    const [pdfURL, setPdfURL] = useState<string | null>(null);
    const prevBrandRef = useRef<string | undefined>();
    const prevLangRef = useRef<string | undefined>();

    let brandTitle;
    let descriptionText;
    switch (brand) {
        case 'audi':
            brandTitle = 'AFS';
            descriptionText = handbook.LABEL_HANDBOOK_TEXT_AUDI;
            break;
        case 'ducati':
            brandTitle = 'DFS';
            descriptionText = handbook.LABEL_HANDBOOK_TEXT_DUCATI;
            break;
        default:
            brandTitle = 'VWFS';
            descriptionText = handbook.LABEL_HANDBOOK_TEXT_VW;
            break;
    }

    /**
     * Fetches the handbook pdf and shows in new tab
     * @returns
     */
    const getHandBookPDF = async () => {
        const newTab = window.open('', '_blank');
        if (!newTab) return false;
        if (
            pdfURL &&
            prevBrandRef.current === brand &&
            prevLangRef.current === language
        ) {
            newTab.location.assign(pdfURL);
        } else {
            prevBrandRef.current = brand;
            prevLangRef.current = language;
            try {
                const docBrand = HANDBOOK_BRANDS.includes(brand)
                    ? brand
                    : HANDBOOK_BRANDS[0];
                const res = await financialService.getDocumentByType(
                    docBrand,
                    isSiteCanadian() ? CANADA : USA,
                    language,
                    FINANCING_HANDBOOK,
                );
                if (res?.data) {
                    const downloadUrl = await getDownloadUrl(res.data);
                    if (downloadUrl) {
                        newTab.location.assign(downloadUrl);
                        setPdfURL(null);
                    } else {
                        const url = URL.createObjectURL(res.data);
                        newTab.location.assign(url);
                        setPdfURL(url);
                    }
                }
            } catch (error) {
                newTab.document.body.innerHTML = ERR_PDF_INNERHTML;
                return false;
            }
        }
    };

    return (
        <div className={containerClass}>
            <div className='title'>
                {handbook.LABEL_HANDBOOK_TITLE.replace('%$', brandTitle)}
            </div>
            <div
                dangerouslySetInnerHTML={{
                    __html: descriptionText,
                }}
                className='description'
            ></div>
            <div className='link-container'>
                <button onClick={getHandBookPDF}>
                    {handbook.LABEL_HANDBOOK_LABEL_LINK}
                </button>
            </div>
        </div>
    );
};

export default Handbook;
